import { useEffect, useRef, useState } from 'react';

/**
 * Slots overlay widget.
 *
 * Subscribes to `slots-trigger` events fan-outed by ws-gateway. On each
 * event: slide the slot machine down, spin three reels, decelerate
 * left → middle → right (~300ms stagger), show result + gem delta,
 * hold, slide back up.
 *
 * 1920x1080 design region scaled to fit any OBS source via CSS
 * `transform: scale(min(100vw/1920, 100vh/1080))`.
 *
 * Queue cap 4 — bursts past that drop oldest to avoid stale backlog.
 * Reels are rendered as a vertical strip of emoji that translates Y to
 * land on the final symbol; CSS animation with eased deceleration.
 */

export type SlotsSymbol = 'seven' | 'diamond' | 'crown' | 'cherry' | 'lemon' | 'plum' | 'skull';

export interface SlotsEvent {
  type: 'slots';
  viewer: string;
  reels: [SlotsSymbol, SlotsSymbol, SlotsSymbol];
  result: 'three_match' | 'two_match' | 'no_match';
  symbol: SlotsSymbol | null;
  gemDelta: number;
  newBalance: number;
  /** Current progressive jackpot pot (post-contribution, or seed after a hit). */
  jackpot?: number | null;
  /** Pot won on this spin (triple-7 only). */
  jackpotPayout?: number | null;
  ts: number;
}

interface Fields {
  displayDurationMs?: number;
  soundsEnabled?: boolean;
}

interface Props {
  streamerId: string;
  event: SlotsEvent | null;
  fields: Fields;
}

const SYMBOL_EMOJI: Record<SlotsSymbol, string> = {
  seven: '7️⃣',
  diamond: '💎',
  crown: '👑',
  cherry: '🍒',
  lemon: '🍋',
  plum: '🍇',
  skull: '💀',
};

// The reel "strip" cycles through symbols. We pad with extras so the spin
// animation looks like a true rolling cylinder before snapping to the
// resolved symbol. Strip is the SYMBOLS list × 4, then the final symbol
// pinned at the end so the easing terminates on it.
const STRIP_ORDER: SlotsSymbol[] = [
  'seven',
  'diamond',
  'crown',
  'cherry',
  'lemon',
  'plum',
  'skull',
];
const SYMBOL_HEIGHT = 200; // px in 1920x1080 design space
const STRIP_REPEAT = 6;

function buildStrip(final: SlotsSymbol): SlotsSymbol[] {
  const arr: SlotsSymbol[] = [];
  for (let i = 0; i < STRIP_REPEAT; i++) arr.push(...STRIP_ORDER);
  arr.push(final);
  return arr;
}

const ANIMATIONS = `
@keyframes slots-slideDown {
  0%   { transform: translate(-50%, -120%); opacity: 0; }
  60%  { transform: translate(-50%, -45%);  opacity: 1; }
  80%  { transform: translate(-50%, -52%);  opacity: 1; }
  100% { transform: translate(-50%, -50%);  opacity: 1; }
}
@keyframes slots-slideUp {
  0%   { transform: translate(-50%, -50%);  opacity: 1; }
  100% { transform: translate(-50%, -120%); opacity: 0; }
}
@keyframes slots-textIn {
  0%   { transform: translateY(20px); opacity: 0; }
  100% { transform: translateY(0);    opacity: 1; }
}
@keyframes slots-shake {
  0%, 100% { transform: translate(-50%, -50%); }
  20%      { transform: translate(calc(-50% - 8px), calc(-50% - 4px)); }
  40%      { transform: translate(calc(-50% + 8px), calc(-50% + 3px)); }
  60%      { transform: translate(calc(-50% - 6px), calc(-50% + 4px)); }
  80%      { transform: translate(calc(-50% + 5px), calc(-50% - 3px)); }
}
@keyframes slots-winPulse {
  0%, 100% { transform: translate(-50%, -50%) scale(1); }
  50%      { transform: translate(-50%, -50%) scale(1.08); }
}
@keyframes slots-particle {
  0%   { transform: translate(0, 0) scale(1); opacity: 1; }
  100% { transform: var(--p-end) scale(0.4);  opacity: 0; }
}
`;

const ENTER_MS = 500;
const SPIN_MS = 1500; // base spin duration; reels stop at ENTER + this + stagger
const REEL_STAGGER = 300;
const EXIT_MS = 400;
const DEFAULT_HOLD_MS = 3000;
const QUEUE_CAP = 4;

interface Playback {
  event: SlotsEvent;
  phase: 'enter' | 'spin' | 'hold' | 'exit';
  // Each reel transitions to its stopped state at staggered times.
  reelStopped: [boolean, boolean, boolean];
}

export function SlotsWidget({ streamerId, event, fields }: Props) {
  void streamerId;
  const holdMs =
    typeof fields.displayDurationMs === 'number'
      ? Math.max(1000, Math.min(15000, fields.displayDurationMs))
      : DEFAULT_HOLD_MS;

  const queueRef = useRef<SlotsEvent[]>([]);
  const playingRef = useRef(false);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const [playback, setPlayback] = useState<Playback | null>(null);

  useEffect(() => {
    if (!event) return;
    if (queueRef.current.some((q) => q.ts === event.ts)) return;
    queueRef.current.push(event);
    if (queueRef.current.length > QUEUE_CAP) {
      queueRef.current.splice(0, queueRef.current.length - QUEUE_CAP);
    }
    pump();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event]);

  useEffect(() => {
    return () => {
      for (const t of timersRef.current) clearTimeout(t);
    };
  }, []);

  function pump() {
    if (playingRef.current) return;
    const next = queueRef.current.shift();
    if (!next) return;
    playingRef.current = true;
    setPlayback({ event: next, phase: 'enter', reelStopped: [false, false, false] });

    const t1 = setTimeout(() => {
      setPlayback((p) => (p ? { ...p, phase: 'spin' } : p));
    }, ENTER_MS);

    // Reels stop staggered.
    const stopBase = ENTER_MS + SPIN_MS;
    const t2 = setTimeout(() => {
      setPlayback((p) =>
        p ? { ...p, reelStopped: [true, p.reelStopped[1], p.reelStopped[2]] } : p,
      );
    }, stopBase);
    const t3 = setTimeout(() => {
      setPlayback((p) =>
        p ? { ...p, reelStopped: [p.reelStopped[0], true, p.reelStopped[2]] } : p,
      );
    }, stopBase + REEL_STAGGER);
    const t4 = setTimeout(
      () => {
        setPlayback((p) =>
          p ? { ...p, reelStopped: [p.reelStopped[0], p.reelStopped[1], true], phase: 'hold' } : p,
        );
      },
      stopBase + REEL_STAGGER * 2,
    );

    const t5 = setTimeout(
      () => {
        setPlayback((p) => (p ? { ...p, phase: 'exit' } : p));
      },
      stopBase + REEL_STAGGER * 2 + holdMs,
    );

    const t6 = setTimeout(
      () => {
        setPlayback(null);
        playingRef.current = false;
        pump();
      },
      stopBase + REEL_STAGGER * 2 + holdMs + EXIT_MS,
    );

    timersRef.current.push(t1, t2, t3, t4, t5, t6);
  }

  if (!playback) return <style>{ANIMATIONS}</style>;

  const { event: e, phase, reelStopped } = playback;
  const isJackpot = e.result === 'three_match' && e.symbol === 'seven';
  const isLoss = e.gemDelta < 0;
  const isWin = e.gemDelta > 0;

  // Machine animation: slide-down on enter, shake on loss, pulse on win,
  // slide-up on exit.
  const machineAnimation =
    phase === 'enter'
      ? `slots-slideDown ${ENTER_MS}ms cubic-bezier(0.22, 1, 0.36, 1) forwards`
      : phase === 'exit'
        ? `slots-slideUp ${EXIT_MS}ms cubic-bezier(0.32, 0, 0.67, 0) forwards`
        : phase === 'hold' && isLoss
          ? `slots-shake 300ms ease 1`
          : phase === 'hold' && isWin
            ? `slots-winPulse 600ms ease 2`
            : undefined;

  // Inner stage: 1920x1080, scaled to fit.
  const fitScale = `min(calc(100vw / 1920), calc(100vh / 1080))`;

  const resultColor = isJackpot ? '#facc15' : isWin ? '#34d399' : isLoss ? '#f87171' : '#cbd5e1';

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        pointerEvents: 'none',
      }}
    >
      <style>{ANIMATIONS}</style>
      <div
        style={{
          position: 'relative',
          width: 1920,
          height: 1080,
          flexShrink: 0,
          transform: `scale(${fitScale})`,
          transformOrigin: 'center center',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 880,
            padding: 32,
            borderRadius: 28,
            background: 'linear-gradient(180deg, #2a1638 0%, #150a22 100%)',
            border: '2px solid rgba(246,85,166,0.45)',
            boxShadow:
              '0 30px 100px rgba(0,0,0,0.6), 0 0 80px rgba(246,85,166,0.25), inset 0 1px 0 rgba(255,255,255,0.1)',
            color: '#fff',
            fontFamily: 'Inter, system-ui, sans-serif',
            animation: machineAnimation,
          }}
        >
          {/* Header */}
          <div
            style={{
              textAlign: 'center',
              fontSize: 42,
              fontWeight: 900,
              letterSpacing: 6,
              textTransform: 'uppercase',
              background: 'linear-gradient(90deg, #f655a6, #fde68a, #f655a6)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              marginBottom: 8,
              textShadow: '0 0 24px rgba(246,85,166,0.4)',
            }}
          >
            🎰 Slots 🎰
          </div>

          {/* Progressive jackpot badge — shown whenever the event carries a
              jackpot value (null = jackpot disabled by streamer, hide). */}
          {typeof e.jackpot === 'number' && e.jackpot > 0 ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 14,
                margin: '0 auto 16px',
                padding: '10px 22px',
                width: 'fit-content',
                borderRadius: 999,
                background: 'linear-gradient(90deg, rgba(250,204,21,0.18), rgba(246,85,166,0.18))',
                border: '1px solid rgba(250,204,21,0.55)',
                boxShadow: '0 0 24px rgba(250,204,21,0.25), inset 0 1px 0 rgba(255,255,255,0.15)',
              }}
            >
              <span
                style={{
                  fontSize: 14,
                  fontWeight: 800,
                  letterSpacing: 3,
                  color: '#fde68a',
                  textTransform: 'uppercase',
                }}
              >
                Jackpot
              </span>
              <span
                style={{
                  fontSize: 26,
                  fontWeight: 900,
                  color: '#facc15',
                  textShadow: '0 0 14px rgba(250,204,21,0.6)',
                }}
              >
                {e.jackpot.toLocaleString()} 💎
              </span>
            </div>
          ) : null}

          {/* Reels */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 16,
              padding: 20,
              borderRadius: 18,
              background: 'rgba(0,0,0,0.5)',
              border: '1px solid rgba(255,255,255,0.06)',
              boxShadow: 'inset 0 4px 16px rgba(0,0,0,0.7)',
            }}
          >
            {[0, 1, 2].map((idx) => (
              <Reel
                key={idx}
                final={e.reels[idx as 0 | 1 | 2]}
                stopped={reelStopped[idx as 0 | 1 | 2]}
                spinning={phase === 'spin' || phase === 'enter'}
                index={idx as 0 | 1 | 2}
              />
            ))}
          </div>

          {/* Result line */}
          {phase === 'hold' || phase === 'exit' ? (
            <div
              style={{
                textAlign: 'center',
                marginTop: 24,
                animation: phase === 'hold' ? 'slots-textIn 350ms ease both' : undefined,
              }}
            >
              <div style={{ fontSize: 22, fontWeight: 600, color: 'rgba(255,255,255,0.85)' }}>
                <span style={{ color: '#f655a6', fontWeight: 800 }}>@{e.viewer}</span> spun!
              </div>
              <div
                style={{
                  fontSize: isJackpot ? 56 : 44,
                  fontWeight: 900,
                  color: resultColor,
                  marginTop: 8,
                  letterSpacing: 0.5,
                  textShadow: `0 0 32px ${resultColor}`,
                }}
              >
                {isJackpot
                  ? `🎉 JACKPOT! +${e.gemDelta} gems`
                  : isWin
                    ? `+${e.gemDelta} gems 🎉`
                    : isLoss
                      ? `${e.gemDelta} gems`
                      : 'Better luck next time'}
              </div>
              {isJackpot && typeof e.jackpotPayout === 'number' && e.jackpotPayout > 0 ? (
                <div
                  style={{
                    marginTop: 6,
                    fontSize: 22,
                    fontWeight: 800,
                    color: '#fde68a',
                    textShadow: '0 0 14px rgba(250,204,21,0.6)',
                  }}
                >
                  Including {e.jackpotPayout.toLocaleString()} 💎 from the pot!
                </div>
              ) : null}
              <div style={{ marginTop: 4, fontSize: 18, color: 'rgba(255,255,255,0.6)' }}>
                Balance: {e.newBalance}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function Reel({
  final,
  stopped,
  spinning,
  index,
}: {
  final: SlotsSymbol;
  stopped: boolean;
  spinning: boolean;
  index: 0 | 1 | 2;
}) {
  // The strip has STRIP_REPEAT × 7 + 1 = 43 entries by default. The final
  // index sits at strip.length - 1. We translate Y so that final lands
  // centered in the window.
  const strip = buildStrip(final);
  const finalIndex = strip.length - 1;
  // While spinning we set transform to a tall negative Y; when stopped we
  // ease into the final position. CSS handles the deceleration via
  // transition timing function on the wrapper.
  const targetY = stopped ? -(finalIndex * SYMBOL_HEIGHT) : -(2 * SYMBOL_HEIGHT);

  const spinDurationMs = SPIN_MS + index * REEL_STAGGER;

  return (
    <div
      style={{
        position: 'relative',
        height: SYMBOL_HEIGHT,
        overflow: 'hidden',
        borderRadius: 12,
        background: 'linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))',
        border: '1px solid rgba(255,255,255,0.08)',
        boxShadow: 'inset 0 0 24px rgba(0,0,0,0.6)',
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: 0,
          willChange: 'transform',
          transform: `translateY(${targetY}px)`,
          transition: stopped
            ? `transform ${spinDurationMs}ms cubic-bezier(0.16, 1, 0.3, 1)`
            : spinning
              ? `transform ${spinDurationMs}ms linear`
              : undefined,
        }}
      >
        {strip.map((sym, i) => (
          <div
            key={i}
            style={{
              height: SYMBOL_HEIGHT,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 130,
              lineHeight: 1,
            }}
          >
            {SYMBOL_EMOJI[sym]}
          </div>
        ))}
      </div>
      {/* Top/bottom soft fade so the reel feels like a window into a cylinder. */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, transparent 18%, transparent 82%, rgba(0,0,0,0.55) 100%)',
          pointerEvents: 'none',
        }}
      />
    </div>
  );
}
